<template>
  <div
    class="party-screen"
    :class="[
      'phase-' + (snap.phase || 'IDLE').toLowerCase(),
      { 'has-scene-bg': !!sceneBgUrl && !isLottery }
    ]"
    :style="rootBgStyle"
  >
    <div v-if="bootError" class="party-screen__error">{{ bootError }}</div>
    <template v-else>
      <div v-if="sceneBgUrl && !isLottery" class="party-screen__bg" :style="sceneBgLayerStyle" aria-hidden="true" />
      <div
        v-if="onlineOverlayStyle"
        class="party-screen__online"
        :style="onlineOverlayStyle"
      >已入场 {{ snap.onlineCount || 0 }} 人</div>
      <transition name="party-fade">
        <div :key="snap.screenSceneKey" class="party-screen__stage" :class="{ 'is-lottery-stage': isLottery }">
          <!-- 首页 / 介绍：纯背景（文案请在背景编辑器中写入） -->
          <div v-if="isHome || isIntro" class="scene scene-display" />

          <!-- 导航 -->
          <div v-else-if="isNav" class="scene scene-nav">
            <h1 class="scene-title">选择互动</h1>
            <div class="nav-cards">
              <div v-for="g in enabledGames" :key="g.key" class="nav-card">{{ g.label || g.key }}</div>
            </div>
            <p class="scene-sub">已入场 {{ snap.onlineCount || 0 }}</p>
          </div>

          <!-- 跑酷预热 -->
          <div v-else-if="isWarmup" class="scene scene-warmup">
            <h1 class="scene-title">点击跑酷 · 准备中</h1>
            <p class="scene-sub">观众端已锁定 · 等待开始</p>
            <p class="scene-tip">在场 {{ snap.onlineCount || 0 }} 人</p>
          </div>

          <!-- 倒计时 / 进行 / 结算 -->
          <div v-else-if="isPlay" class="scene scene-play">
            <div v-if="snap.phase === 'COUNTDOWN'" class="countdown">{{ countdownLeft }}</div>
            <template v-else>
              <h1 class="scene-title">{{ snap.phase === 'SETTLED' ? '本局结果' : '激烈竞速中' }}</h1>
              <div class="rank-board">
                <div v-for="p in topList" :key="p.uid" class="rank-item">
                  <span class="rank-no">{{ p.rank }}</span>
                  <img v-if="p.avatar" class="rank-avatar" :src="p.avatar" alt="" />
                  <span class="rank-nick">{{ p.nick || p.uid }}</span>
                  <span class="rank-score">{{ p.score }}</span>
                </div>
              </div>
            </template>
          </div>

          <lottery-stage
            v-else-if="isLottery"
            :guests="lotteryGuests"
            :draft="lotteryDraft"
            :h5="lotteryH5"
            :online-count="snap.onlineCount || 0"
          />

          <div v-else class="scene scene-display" />
        </div>
      </transition>

      <!-- 简易粒子层：切换时闪一下（完整粒子可后续增强） -->
      <div v-if="showBurst" class="particle-burst" aria-hidden="true" />
    </template>
  </div>
</template>

<script>
import { partyScreenBootstrap, partySnapshot } from '@/api/party'
import { getImageUrl } from '@/utils/image'
import LotteryStage from './lotteryStage.vue'

export default {
  name: 'PartyScreen',
  components: { LotteryStage },
  data() {
    return {
      bootError: '',
      activityCode: '',
      signed: false,
      snap: {
        screenSceneKey: 'home',
        phase: 'IDLE',
        onlineCount: 0,
        topPlayers: [],
        partyConfig: {},
        countdownEndAt: 0,
        rev: 0
      },
      lastRev: 0,
      lastTransitionId: '',
      showBurst: false,
      pollTimer: null,
      tickTimer: null,
      now: Date.now(),
    }
  },
  computed: {
    scenes() {
      return (this.snap.partyConfig && this.snap.partyConfig.scenes) || []
    },
    currentScene() {
      return this.scenes.find(s => s.sceneKey === this.snap.screenSceneKey) || null
    },
    enabledGames() {
      const games = (this.snap.partyConfig && this.snap.partyConfig.games) || []
      return games.filter(g => g.enabled !== false)
    },
    isHome() {
      return this.snap.screenSceneKey === 'home'
    },
    isIntro() {
      return String(this.snap.screenSceneKey || '').startsWith('intro.')
    },
    isNav() {
      return this.snap.screenSceneKey === 'nav'
    },
    isWarmup() {
      return String(this.snap.screenSceneKey || '').endsWith('.warmup')
    },
    isPlay() {
      return String(this.snap.screenSceneKey || '').endsWith('.play')
    },
    isLottery() {
      const key = String(this.snap.screenSceneKey || '')
      const scene = this.currentScene
      return key.indexOf('lottery') >= 0 || (scene && scene.gameKey === 'lottery') || this.snap.phase === 'LOTTERY'
    },
    lotteryDraft() {
      return this.snap.lotteryDraft || null
    },
    lotteryH5() {
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      return h5
    },
    /** 非抽奖场景：读取当前场景配置的大屏背景图 */
    sceneBgUrl() {
      if (this.isLottery) return ''
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      const raw = h5.bg || ''
      return raw ? getImageUrl(raw) : ''
    },
    sceneBgLayerStyle() {
      if (!this.sceneBgUrl) return {}
      return {
        backgroundImage: 'url(' + this.sceneBgUrl + ')'
      }
    },
    /** 背景编辑器配置的入场人数叠层（1920×1080 坐标 → 百分比） */
    onlineOverlayStyle() {
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      const o = h5.onlineOverlay
      if (!o || typeof o !== 'object') return null
      const fontSize = Math.max(16, Number(o.fontSize) || 36)
      return {
        left: ((Number(o.x) || 0) / 1920 * 100) + '%',
        top: ((Number(o.y) || 0) / 1080 * 100) + '%',
        fontSize: 'clamp(14px, ' + (fontSize / 1920 * 100) + 'vw, ' + fontSize + 'px)',
        color: o.color || '#ffffff'
      }
    },
    rootBgStyle() {
      // 有图时去掉默认渐变，避免盖住背景层
      if (this.sceneBgUrl && !this.isLottery) {
        return { background: '#0b1020' }
      }
      return {}
    },
    lotteryGuests() {
      // 抽奖球名单：优先有昵称的入场玩家（后端 topPlayers 含仅入场无积分）
      const list = this.snap.topPlayers || []
      return list.filter(g => g && (g.nick || g.nickName || g.uid))
    },
    topList() {
      return (this.snap.topPlayers || []).slice(0, 20)
    },
    countdownLeft() {
      const end = Number(this.snap.countdownEndAt || 0)
      if (!end || this.snap.phase !== 'COUNTDOWN') return 0
      return Math.max(0, Math.ceil((end - this.now) / 1000))
    }
  },
  async mounted() {
    const q = this.$route.query || {}
    this.activityCode = String(q.activity_code || q.activityCode || '').trim()
    if (!this.activityCode) {
      this.bootError = '缺少 activity_code'
      return
    }
    try {
      if (q.sign && q.ts) {
        const res = await partyScreenBootstrap({
          activity_code: this.activityCode,
          store_code: q.store_code || undefined,
          ts: q.ts,
          sign: q.sign
        })
        this.applySnap(this.unwrap(res), true)
        this.signed = true
      } else {
        const res = await partySnapshot({ activityCode: this.activityCode })
        this.applySnap(this.unwrap(res), true)
      }
    } catch (e) {
      this.bootError = (e && e.message) || '大屏鉴权/加载失败'
      return
    }
    this.pollTimer = setInterval(() => this.poll(), 800)
    this.tickTimer = setInterval(() => {
      this.now = Date.now()
    }, 200)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.tickTimer) clearInterval(this.tickTimer)
  },
  methods: {
    unwrap(res) {
      if (!res) return null
      if (res.data != null && (res.success === true || res.status === 200 || res.code === 200 || res.ok)) {
        return res.data
      }
      if (res.screenSceneKey || res.phase) return res
      return res.data || res
    },
    applySnap(data, first) {
      if (!data) return
      const rev = Number(data.rev || 0)
      // rev 未变则跳过整页重渲染（抽奖球不被 800ms 轮询打断）
      if (!first && rev && rev === this.lastRev) {
        if (data.onlineCount != null && data.onlineCount !== this.snap.onlineCount) {
          this.$set(this.snap, 'onlineCount', data.onlineCount)
        }
        return
      }
      const prevRev = this.lastRev
      const next = Object.assign({}, this.snap, data)
      if (data.partyConfig) next.partyConfig = data.partyConfig
      if (data.lotteryDraft !== undefined) next.lotteryDraft = data.lotteryDraft
      if (data.screenSceneKey != null) next.screenSceneKey = data.screenSceneKey
      if (data.phase != null) next.phase = data.phase
      this.snap = next
      const tr = data.transition || {}
      const trId = tr.id || ''
      const targets = tr.targets || []
      if (!first && rev && rev !== prevRev && targets.indexOf('screen') >= 0 && trId && trId !== this.lastTransitionId) {
        this.lastTransitionId = trId
        this.burst()
      }
      this.lastRev = rev || this.lastRev
    },
    burst() {
      this.showBurst = true
      setTimeout(() => {
        this.showBurst = false
      }, 700)
    },
    async poll() {
      if (this.bootError) return
      try {
        const res = await partySnapshot({ activityCode: this.activityCode })
        this.applySnap(this.unwrap(res), false)
      } catch (e) {
        if (!this._pollErrLogged) {
          this._pollErrLogged = true
          console.warn('[party-screen] snapshot poll failed', e)
        }
      }
    }
  }
}
</script>

<style scoped>
.party-screen {
  min-height: 100vh;
  background: radial-gradient(ellipse at center, #1a2740 0%, #0b1020 70%);
  color: #f5f7fa;
  overflow: hidden;
  position: relative;
}
.party-screen__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}
.party-screen__online {
  position: absolute;
  z-index: 3;
  font-weight: 600;
  letter-spacing: 1px;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.65);
  transform-origin: left top;
}
.party-screen__veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(8, 12, 24, 0.35) 0%,
    rgba(8, 12, 24, 0.2) 40%,
    rgba(8, 12, 24, 0.45) 100%
  );
  pointer-events: none;
}
.party-screen__error {
  padding: 48px;
  font-size: 24px;
  text-align: center;
  color: #f56c6c;
  position: relative;
  z-index: 2;
}
.party-screen__stage {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  z-index: 2;
}
.party-screen__stage.is-lottery-stage {
  padding: 0;
  display: block;
  position: relative;
}
.scene {
  width: 100%;
  max-width: 1100px;
  text-align: center;
}
.party-screen.has-scene-bg .scene-title,
.party-screen.has-scene-bg .scene-sub,
.party-screen.has-scene-bg .scene-tip {
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);
}
.scene-title {
  font-size: 56px;
  margin: 0 0 16px;
  letter-spacing: 2px;
}
.scene-sub {
  font-size: 28px;
  opacity: 0.85;
  margin: 8px 0;
}
.scene-tip {
  font-size: 20px;
  opacity: 0.6;
  margin-top: 24px;
}
.nav-cards {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 32px 0;
  flex-wrap: wrap;
}
.nav-card {
  min-width: 180px;
  padding: 28px 24px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  font-size: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.countdown {
  font-size: 180px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 0 40px rgba(64, 158, 255, 0.6);
}
.rank-board {
  margin: 24px auto 0;
  max-width: 720px;
  text-align: left;
}
.rank-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  font-size: 22px;
}
.rank-no {
  width: 36px;
  font-weight: 700;
  color: #79bbff;
}
.rank-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.rank-nick {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-score {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.particle-burst {
  pointer-events: none;
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.35) 0 2px, transparent 3px),
    radial-gradient(circle at 70% 40%, rgba(121, 187, 255, 0.5) 0 2px, transparent 3px),
    radial-gradient(circle at 40% 80%, rgba(255, 200, 100, 0.45) 0 2px, transparent 3px),
    radial-gradient(circle at 85% 70%, rgba(255, 255, 255, 0.3) 0 2px, transparent 3px);
  background-size: 100% 100%;
  animation: burst 0.7s ease-out forwards;
  z-index: 20;
}
@keyframes burst {
  0% { opacity: 0; transform: scale(0.92); }
  30% { opacity: 1; }
  100% { opacity: 0; transform: scale(1.08); }
}
.party-fade-enter-active,
.party-fade-leave-active {
  transition: opacity 0.35s ease;
}
.party-fade-enter,
.party-fade-leave-to {
  opacity: 0;
}
</style>
